import { isFileNameAllowed, parseAcceptExtensions } from '@utils/file-types';

import { isRealFile } from './utils';

type FileTypeValidator = (value: unknown) => true | string;

// A validator is a pure function of its accept list, and it is built in a render
// body — so hand back the same function for the same list. A fresh closure each
// render defeats the deep-equal memo in `useFormField` and re-registers the field.
const validatorCache = new Map<string, FileTypeValidator>();

export const createFileTypeValidator = (accept: string): FileTypeValidator => {
  const cached = validatorCache.get(accept);
  if (cached) return cached;

  const allowedExtensions = parseAcceptExtensions(accept);
  // Only files the user just picked are judged; a previously saved answer is a
  // reference, not a File, and the server likewise only inspects request.FILES.
  const validator: FileTypeValidator = (value) => {
    // An accept list with no usable extension restricts nothing — the same call
    // `allowed_extensions_for_field` makes, so a field cannot end up unfillable on
    // one side and unrestricted on the other.
    if (allowedExtensions.length === 0) return true;
    const entries = Array.isArray(value) ? value : [value];
    const hasDisallowedFile = entries
      .filter(isRealFile)
      .some((file) => !isFileNameAllowed(file.name, allowedExtensions));
    return hasDisallowedFile ? 'file-type' : true;
  };

  validatorCache.set(accept, validator);
  return validator;
};
